import React, { useState, useEffect } from "react";
import { Alert, Button, Box, Collapse, Container } from "@mui/material";
import { LocationOn, Close } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "store";
import { setGeoCords, setState, setCity, setCountry, setFetchedGeoLoc } from "store/actions";
import { getLocation } from "../Hooks/utils/getLocation";

interface LocationBannerProps {
  onDismiss?: () => void;
}

const LocationBanner: React.FC<LocationBannerProps> = ({ onDismiss }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  
  const dispatch = useDispatch();
  const location = useSelector((state: RootState) => state.location);
  const user = useSelector((state: RootState) => state.user.user);

  useEffect(() => {
    const checkLocationPermission = async () => {
      if (!navigator.geolocation || dismissed || location.fetched) {
        return;
      }

      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        
        if (permission.state === 'denied') {
          setPermissionDenied(true);
          setShowBanner(true);
        } else if (permission.state === 'prompt') {
          setPermissionDenied(false);
          setShowBanner(true);
        }
      } catch (error) {
        setShowBanner(true);
      }
    };

    if (user) {
      checkLocationPermission();
    }
  }, [user, dismissed, location.fetched]);

  const handleEnableLocation = async () => {
    setRequesting(true);
    
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 600000
        });
      });

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      
      dispatch(setGeoCords({ lng, lat }));
      dispatch(setFetchedGeoLoc());

      try {
        const { city, state, country } = await getLocation(lat, lng);
        dispatch(setCity(city));
        dispatch(setState(state));
        dispatch(setCountry(country));
      } catch (err) {
        console.log("Error setting geolocation details", err);
      }

      setShowBanner(false);
      setDismissed(true);
    } catch (error) {
      console.error("Error getting location:", error);
      if (error instanceof GeolocationPositionError && error.code === error.PERMISSION_DENIED) {
        setPermissionDenied(true);
      }
    } finally {
      setRequesting(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    onDismiss?.();
  };

  const handleOpenSettings = () => {
    alert('To enable location access:\n\n1. Click the location icon in your browser\'s address bar\n2. Select "Allow" for location access\n3. Refresh the page\n\nOr go to your browser settings and allow location access for this site.');
  };

  if (!showBanner || !user) {
    return null;
  }

  return (
    <Collapse in={showBanner}>
      <Box sx={{ 
        width: '100%',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Container maxWidth="lg" sx={{ py: 1 }}>
          <Alert
            severity="info"
            icon={<LocationOn />}
            action={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {permissionDenied ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleOpenSettings}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Open Settings
                  </Button>
                ) : (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleEnableLocation}
                    disabled={requesting}
                    sx={{ fontWeight: 'bold' }}
                  >
                    {requesting ? 'Requesting...' : 'Enable Location'}
                  </Button>
                )}
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleDismiss}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  <Close fontSize="small" />
                </Button>
              </Box>
            }
            sx={{
              '& .MuiAlert-message': {
                flex: 1,
                alignSelf: 'center'
              }
            }}
          >
            {permissionDenied 
              ? 'Location access was denied. Please enable it in your browser settings to see local poker games.'
              : 'Location access helps us find poker games near you. Please enable location services to see local games.'
            }
          </Alert>
        </Container>
      </Box>
    </Collapse>
  );
};

export default LocationBanner;